/**
 * Reusable JSON-LD structured data component.
 *
 * Renders a <script type="application/ld+json"> tag with the given schema.
 * Works in both Server Components and Client Components — it's sync, so
 * client renderers can use it too.
 *
 * ## CSP nonce
 *
 * `<script type="application/ld+json">` elements are subject to `script-src`
 * even though they don't execute. Under the nonce+strict-dynamic CSP in
 * `lib/csp.ts`, modern browsers block unnonced inline scripts — and
 * Googlebot (Chromium-based) would stop seeing the structured data.
 *
 * Server callers should pass the per-request nonce:
 *
 *   import { headers } from "next/headers"
 *   const nonce = (await headers()).get("x-nonce") ?? undefined
 *   return <JsonLd data={...} nonce={nonce} />
 *
 * Or, for a client-component subtree, read the nonce once in the server
 * parent and pass it as a prop down to the client, which forwards it to
 * `<JsonLd>`. See `app/(frontend)/[country]/[locale]/pricing/page.tsx` +
 * `pricing-client.tsx` for the pattern.
 *
 * @example
 * <JsonLd data={{ "@type": "Organization", name: "Ahead Health", ... }} nonce={nonce} />
 */

import type { Thing, WithContext } from "schema-dts"
import { findReviewMarkup } from "@/lib/json-ld-review-ban"

type JsonLdProps<T extends Thing> = {
  data: WithContext<T>
  nonce?: string
}

export function JsonLd<T extends Thing>({ data, nonce }: JsonLdProps<T>) {
  // Fail loud in dev and tests; production renders what it is given and the
  // e2e sweep in e2e/review-figures.spec.ts guards the deployed pages.
  if (process.env.NODE_ENV !== "production") {
    const hits = findReviewMarkup(data)
    if (hits.length > 0) {
      throw new Error(
        `JSON-LD must not carry review markup (${hits.join(", ")}); see lib/json-ld-review-ban.ts`
      )
    }
  }
  return (
    <script
      type="application/ld+json"
      nonce={nonce}
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  )
}
